<script>
import InCellVisitsService from '@/services/InCellVisitsService';
import { getVisitRequestedDates } from '@/util/helpers';

export default {
  name: 'InCellVisits',
  data() {
    return {
      loading: true,
      visits: [],
      fields: [
        { label: 'Requested On', key: 'requestedOn' },
        { label: 'Visitor', key: 'visitor' },
        { label: 'Resident', key: 'resident' },
        { label: 'Relationship', key: 'relationship' },
        { label: 'Booked Slot', key: 'bookedSlot' },
        { label: 'Status', key: 'status' },
      ],
      filters: {
        status: null,
        visitorFirstName: '',
        visitorLastName: '',
        prisonerNumber: '',
        before: null,
        after: null,
        sort: 'booked_slot.start_date_time',
      },
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      sortOptions: [
        { value: 'booked_slot.start_date_time', text: 'Booked Slot (ascending)' },
        { value: '-booked_slot.start_date_time', text: 'Booked Slot (descending)' },
        { value: 'created_at', text: 'Requested On (ascending)' },
        { value: '-created_at', text: 'Requested On (descending)' },
      ],
      statusOptions: [
        { value: null, text: 'All' },
        { value: 'Requested', text: 'Requested' },
        { value: 'AwaitingConfirmation', text: 'Awaiting Confirmation' },
        { value: 'Approved', text: 'Approved' },
        { value: 'Completed', text: 'Completed' },
        { value: 'Rejected', text: 'Rejected' },
        { value: 'Cancelled', text: 'Cancelled' },
      ],
      debounceTimer: null,
    };
  },
  computed: {
    pageTitle() {
      switch (this.filters.status) {
        case 'Requested':
          return 'Requested In-Room Calls';
        case 'AwaitingConfirmation':
          return 'In-Room Calls Awaiting Confirmation';
        case 'Approved':
          return 'Approved In-Room Calls';
        case 'Completed':
          return 'Completed In-Room Calls';
        case 'Rejected':
          return 'Rejected In-Room Calls';
        case 'Cancelled':
          return 'Cancelled In-Room Calls';
        default:
          return 'All In-Room Calls';
      }
    },
  },
  async mounted() {
    const { status } = this.$route.meta;
    this.filters.status = status || null;
    try {
      await this.getVisits();
      this.loading = false;
    } catch (e) {
      this.$router.go(-1);
    }
  },
  methods: {
    async getVisits() {
      try {
        const response = await InCellVisitsService.getVisitsPaginated({
          ...this.filters,
          skip: (this.currentPage - 1) * this.perPage,
          limit: this.perPage,
        });
        this.visits = response.data.data;
        this.totalCount = response.data.count;
      } catch (e) {
        console.error('Error getting in-cell calls: ', e);
        const res = e.response;
        let errorText = 'Error fetching in-room calls, please try again.';
        if (res && res.data.error) {
          errorText = res.data.error;
        }
        this.$toast.error(errorText);
        throw e
      }
    },
    search() {
      this.currentPage = 1;
      this.debounce(this.getVisits)();
    },
    updateFilter() {
      this.currentPage = 1;
      this.getVisits();
    },
    debounce(func, timeout = 300) {
      return (...args) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    clearFilters() {
      this.currentPage = 1;
      this.filters = {
        status: null,
        visitorFirstName: '',
        visitorLastName: '',
        prisonerNumber: '',
        before: null,
        after: null,
        sort: 'created_at',
      };
      this.getVisits();
    },
    pushDetailsPage(visit) {
      this.$store.dispatch('inCellVisits/setVisit', visit);
      this.$router.push({ name: 'in-cell-visit-details', params: { id: visit._id } });
    },
    getStatus(item) {
      if (item.status === 'Funded' || item.status === 'AwaitingPayment') {
        return 'Awaiting Confirmation';
      }
      return item.status;
    },
    getSlotDate(visit) {
      const { requestedFor1 } = getVisitRequestedDates(visit);
      return requestedFor1;
    },
  },
};
</script>

<template>
  <div v-if="loading">
    <div class="col text-center mt-5">
      <span class="fas fa-spinner fa-spin" style="font-size: 2em;" />
    </div>
  </div>
  <div v-else>
    <b-card no-body class="mb-0">
      <b-row class="p-2 d-flex justify-content-between">
        <b-col>
          <h2>
            {{ pageTitle }}
          </h2>
        </b-col>
        <b-col>
          <b-btn class="float-right" variant="outline-primary" :to="{ name: 'book-in-cell-visit' }">
            Book In-Room Call
          </b-btn>
        </b-col>
      </b-row>
      <b-col class="p-2">
        <b-row class="mb-2 align-items-end">

          <!--Visitor first name-->
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label>
              Visitor Firstname
            </label>
            <b-input-group>
              <b-form-input
                v-model="filters.visitorFirstName"
                class="d-inline-block"
                placeholder="Search visitor firstname"
                @keydown.enter="search"
                @input="search"
              />
            </b-input-group>
          </b-col>

          <!--Visitor last name-->
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label>
              Visitor Lastname
            </label>
            <b-input-group>
              <b-form-input
                v-model="filters.visitorLastName"
                class="d-inline-block"
                placeholder="Search visitor lastname"
                @keydown.enter="search"
                @input="search"
              />
            </b-input-group>
          </b-col>

          <!--Prisoner number-->
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label>
              Resident ID
            </label>
            <b-input-group>
              <b-form-input
                v-model="filters.prisonerNumber"
                class="d-inline-block"
                placeholder="Search resident id"
                @keydown.enter="search"
                @input="search"
              />
            </b-input-group>
          </b-col>
        </b-row>
        <b-row class="mb-2 align-items-end">
          <!--After date-->
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label>Booked For (after) <feather-icon v-b-tooltip.hover title="Show only records booked on or after this date." icon="InfoIcon" size="14" /></label>
            <b-form-datepicker
              v-model="filters.after"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" close-button
              cy="before"

              reset-button
              today-button
              @input="updateFilter"
            />
          </b-col>

          <!--Before date-->
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label>Booked For (before) <feather-icon v-b-tooltip.hover title="Show only records booked before this date." icon="InfoIcon" size="14" /></label>
            <b-form-datepicker
              v-model="filters.before"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" close-button
              cy="before"

              reset-button
              today-button
              @input="updateFilter"
            />
          </b-col>

          <!--Sort-->
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label>Sort</label>
            <b-form-select
              v-model="filters.sort"
              :options="sortOptions"
              cy="sort"
              @change="updateFilter"
            />
          </b-col>

          <!--Status-->
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label>
              Status
            </label>
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
              cy="sort"
              @change="updateFilter"
            />
          </b-col>
        </b-row>
        <b-row class="mb-2 d-flex justify-content-end">
          <!--Clear filters-->
          <b-col cols="12" md="6" lg="3">
            <b-btn class="w-100" @click="clearFilters">
              Clear Filters
            </b-btn>
          </b-col>
        </b-row>
      </b-col>
      <b-table
        :current-page="currentPage"
        :fields="fields"
        :items="visits"
        :responsive="true"
        class="data-table mb-0"
        hover
        show-empty
        @row-clicked="pushDetailsPage"
      >
        <template #cell(requestedOn)="data">
          {{ data.item.created_at | formatDateTime }}
        </template>
        <template #cell(location)="data">
          {{ data.item.prison.name }}
        </template>
        <template #cell(visitor)="data">
          {{ data.item.visitor.firstname }} {{ data.item.visitor.lastname }}
        </template>
        <template #cell(resident)="data">
          {{ data.item.prisoner.firstname }} {{ data.item.prisoner.lastname }} ({{
            data.item.prisoner.prisoner_number
          }})
        </template>
        <template #cell(relationship)="data">
          {{ data.item.prisoner.relationship_to_booker }}
        </template>
        <template #cell(bookedSlot)="data">
          {{ getSlotDate(data.item) }}
        </template>
        <template #cell(status)="data">
          {{ getStatus(data.item) }}
        </template>
      </b-table>

      <b-row v-if="visits && visits.length">
        <b-col class="m-1">
          <b-pagination
            v-model="currentPage"
            :per-page="perPage"
            :total-rows="totalCount"
            class="my-0"
            @input="getVisits"
          />
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>
